import { useCallback, useMemo, useState, type CSSProperties, type RefObject } from 'react';
import { Card } from '../../components/Card';
import { PagePreviewControls } from './PagePreviewControls';
import { PagePreviewsFlow } from './PagePreviewsFlow';
import { PagePreviewsStacked } from './PagePreviewsStacked';
import { usePagePreviewControls } from './usePagePreviewControls';
import { PagePreviewHoverProvider } from '../../components/pagePreview/usePagePreviewHover';
import type { ClusterGroup } from './previewHelpers';
import type { CategoryPages } from './types';

export function PagePreviews({
  pages,
  category,
  scrollContainerRef,
}: {
  pages: CategoryPages;
  category: string | undefined;
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
}) {
  const { size, setSize, compact, setCompact, tint, setTint, layout, setLayout } = usePagePreviewControls();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const onExpand = useCallback((clusterId: string) => {
    setExpanded((prev) => new Set(prev).add(clusterId));
  }, []);

  const pagesGrouped = useMemo<ClusterGroup[]>(() => {
    return Object.entries(
      pages.reduce(
        (acc, page) => {
          (acc[page.clusterId] ??= []).push(page);
          return acc;
        },
        {} as Record<string, CategoryPages>,
      ),
    ).sort((a, b) => a[1].length - b[1].length);
  }, [pages]);

  // Larger thumbnails get a higher-res source so they don't blur on upscale.
  const resize = size <= 130 ? 'x150' : 'x300';
  const layoutProps = { pagesGrouped, category, tint, compact, expanded, onExpand, resize };

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">Page Previews</h2>
        <PagePreviewControls
          size={size}
          setSize={setSize}
          compact={compact}
          setCompact={setCompact}
          tint={tint}
          setTint={setTint}
          layout={layout}
          setLayout={setLayout}
        />
      </div>

      {/* Bounded, internally-scrollable so a 4k-page / 300-cluster category doesn't make the
          card (and the page) enormous. --thumb-w is read by both layouts to size thumbnails. */}
      <div
        ref={scrollContainerRef}
        className="relative max-h-[75vh] overflow-y-auto pr-1"
        style={{ '--thumb-w': `${size}px` } as CSSProperties}
      >
        <PagePreviewHoverProvider>
          {layout === 'flow' ? <PagePreviewsFlow {...layoutProps} /> : <PagePreviewsStacked {...layoutProps} />}
        </PagePreviewHoverProvider>
      </div>
    </Card>
  );
}
